import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiClient } from "../api/client";
import { tokenStore } from "../api/tokenStore";
import { useAuth } from "../hooks/useAuth";
import { LineField } from "../components/LineField";
import { FormHeader, CenteredForm } from "../components/FormHeader";
import { passwordProblem } from "../utils/passwordPolicy";
import { C, font, space } from "../styles/tokens";
import { BrandButton } from "../components/brand";

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const auth = useAuth();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [validating, setValidating] = useState(Boolean(token));
  const [invalidReason, setInvalidReason] = useState(null);

  // Validate reset token on load
  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }

    let isMounted = true;

    // Fast client-side inspection for expiration
    try {
      const parts = token.split(".");
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          setInvalidReason(
            "This password reset link is no longer valid. Request a new one.",
          );
          setValidating(false);
          return;
        }
      }
    } catch {
      // If parsing fails, allow backend endpoint to validate
    }

    // Backend token verification
    apiClient
      .get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then(() => {
        if (isMounted) {
          setValidating(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setInvalidReason(
            err?.message ||
              "This password reset link is no longer valid. Request a new one.",
          );
          setValidating(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (!password) {
      setError("Please enter a new password.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const passwordError = passwordProblem(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post("/auth/reset-password", { token, password });

      // Invalidate current browser session
      tokenStore.clearAccessToken();
      try {
        if (auth?.logout) {
          auth.logout().catch(() => {});
        }
      } catch {
        // Ignore logout errors
      }

      setSuccess(true);
    } catch (err) {
      const msg =
        err?.message || "Failed to reset password. The link may have expired.";
      setError(msg);

      // If token is invalid, expired, or already used, offer replacement link
      if (
        msg.includes("no longer valid") ||
        msg.includes("already been used") ||
        msg.includes("invalid or has expired") ||
        msg.includes("User not found")
      ) {
        setInvalidReason(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <CenteredForm>
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>
            Invalid Reset Link
          </h2>
          <p style={{ fontSize: 14, color: C.textMed, marginBottom: 20 }}>
            This password reset link is invalid or incomplete.
          </p>
          <Link to="/forgot-password" style={actionButtonStyle}>
            Request a New Reset Link
          </Link>
        </div>
      </CenteredForm>
    );
  }

  if (validating) {
    return (
      <CenteredForm>
        <div style={{ textAlign: "center", padding: "30px 0" }}>
          <p style={{ fontSize: 14, color: C.textMed }}>
            Verifying your reset link...
          </p>
        </div>
      </CenteredForm>
    );
  }

  if (invalidReason) {
    return (
      <CenteredForm>
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>
            Reset Link No Longer Valid
          </h2>
          <p
            style={{
              fontSize: 14,
              color: C.textMed,
              lineHeight: 1.5,
              marginBottom: 20,
            }}
          >
            {invalidReason}
          </p>
          <Link to="/forgot-password" style={actionButtonStyle}>
            Request a New Reset Link
          </Link>
        </div>
      </CenteredForm>
    );
  }

  return (
    <CenteredForm>
      {success ? (
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: C.greenLight || "#E6F4EA",
              color: C.green || "#137333",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>
            Password Reset Complete
          </h2>
          <p style={{ fontSize: 14, color: C.textMuted, marginBottom: 20 }}>
            Your password has been reset successfully. Existing sessions have
            been signed out. You can now sign in with your new password.
          </p>
          <Link to="/login" style={{ textDecoration: "none" }}>
            <BrandButton variant="primary" fullWidth>
              Sign in
            </BrandButton>
          </Link>
        </div>
      ) : (
        <>
          <FormHeader
            title="Set new password"
            subtitle="Choose a strong password with at least 8 characters, including uppercase, lowercase, and numbers."
          />
          <form onSubmit={handleSubmit}>
            <LineField
              label="New Password"
              type="password"
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              autoComplete="new-password"
              required
            />
            <LineField
              label="Confirm Password"
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={submitting}
              autoComplete="new-password"
              required
            />
            {error && (
              <div role="alert" style={errorBoxStyle}>
                <div>{error}</div>
                {(error.includes("no longer valid") ||
                  error.includes("already been used") ||
                  error.includes("invalid or has expired") ||
                  error.includes("User not found")) && (
                  <div style={{ marginTop: 8 }}>
                    <Link
                      to="/forgot-password"
                      style={{
                        color: C.danger,
                        fontWeight: 600,
                        textDecoration: "underline",
                      }}
                    >
                      Request a New Reset Link
                    </Link>
                  </div>
                )}
              </div>
            )}
            <div style={{ marginTop: space.lg }}>
              <BrandButton
                type="submit"
                variant="primary"
                fullWidth
                loading={submitting}
              >
                {submitting ? "Resetting..." : "Reset password"}
              </BrandButton>
            </div>
          </form>
          <div
            style={{
              marginTop: space.lg,
              textAlign: "center",
              fontSize: 13,
              color: C.textMuted,
            }}
          >
            <Link
              to="/forgot-password"
              style={{ color: C.primary, fontWeight: 600, textDecoration: "none" }}
            >
              Request a new reset link
            </Link>
          </div>
        </>
      )}
    </CenteredForm>
  );
}

const errorBoxStyle = {
  fontSize: 13,
  color: C.danger,
  background: C.dangerTint,
  padding: "10px 12px",
  borderRadius: 8,
  marginTop: 8,
};
